import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

AnimatedBear.propTypes = {
  isPasswordFocused: PropTypes.bool,
  textLength: PropTypes.number,
};

export default function AnimatedBear({ isPasswordFocused, textLength = 0 }) {
  // Calculate eye pupil position based on input length
  // We constrain it so pupils don't pop out of the eyes
  const eyeX = useMemo(() => {
    if (isPasswordFocused) return 0;
    const maxOffset = 5;
    const offset = Math.min(maxOffset, textLength * 0.4);
    return offset - 2.5; // shift from -2.5 to 2.5
  }, [textLength, isPasswordFocused]);

  const eyeY = isPasswordFocused ? 0 : 2;

  return (
    <Box sx={{ textAlign: 'center', mb: 3 }}>
      <svg
        width="120"
        height="120"
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <clipPath id="eye-clip">
            <circle cx="0" cy="0" r="10" />
          </clipPath>
        </defs>

        {/* Ears */}
        <circle cx="60" cy="50" r="18" fill="#1e3a8a" />
        <circle cx="60" cy="50" r="10" fill="#bfdbfe" />
        <circle cx="140" cy="50" r="18" fill="#1e3a8a" />
        <circle cx="140" cy="50" r="10" fill="#bfdbfe" />

        {/* Head */}
        <circle cx="100" cy="100" r="60" fill="#2563eb" />

        {/* Eyes Base */}
        <circle cx="75" cy="85" r="12" fill="white" />
        <circle cx="125" cy="85" r="12" fill="white" />

        {/* Pupils (Animated) */}
        {!isPasswordFocused && (
          <g>
            <circle cx={75 + eyeX} cy={85 + eyeY} r="6" fill="#111827" />
            <circle cx={125 + eyeX} cy={85 + eyeY} r="6" fill="#111827" />
          </g>
        )}

        {/* Closed Eyes (when password focused) */}
        {isPasswordFocused && (
          <g>
            <path d="M 65 85 Q 75 95 85 85" stroke="#111827" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M 115 85 Q 125 95 135 85" stroke="#111827" strokeWidth="3" fill="none" strokeLinecap="round" />
          </g>
        )}

        {/* Snout */}
        <ellipse cx="100" cy="115" rx="22" ry="16" fill="#bfdbfe" />
        
        {/* Nose */}
        <path d="M 90 110 Q 100 105 110 110 L 100 120 Z" fill="#111827" />
        
        {/* Mouth */}
        <path d="M 95 120 Q 100 125 105 120" stroke="#111827" strokeWidth="2" fill="none" strokeLinecap="round" />

        {/* Arms covering eyes (Animated via CSS transition) */}
        <g
          style={{
            transform: isPasswordFocused ? 'translateY(-35px)' : 'translateY(40px)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Left Arm */}
          <circle cx="65" cy="130" r="22" fill="#1e40af" />
          <path d="M 55 125 Q 65 115 75 125" stroke="#1e3a8a" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Right Arm */}
          <circle cx="135" cy="130" r="22" fill="#1e40af" />
          <path d="M 125 125 Q 135 115 145 125" stroke="#1e3a8a" strokeWidth="2" fill="none" strokeLinecap="round" />
        </g>
      </svg>
      <Typography variant="caption" sx={{ display: 'block', mt: -1, color: 'text.secondary', fontStyle: 'italic' }}>
        {isPasswordFocused ? 'I can\'t look! 🙈' : 'Watching you type... 👀'}
      </Typography>
    </Box>
  );
}
